import { BASE_URL } from "../config/api";
import { jwtDecode } from "jwt-decode";
import { fetchUserById } from "./userService";

// Helper: get logged user email
const getUserEmailForLogging = async () => {
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      const user = await fetchUserById(decoded.id);
      return user.user_email;
    }
  } catch (error) {
    console.error("Error getting user email for logging:", error);
  }
  return "unknown";
};

// Create Blog
export const createBlog = async (formData) => {
  const token = localStorage.getItem("token");

  const response = await fetch(`${BASE_URL}/blog/createBlog`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const result = await response.json();
  if (!response.ok) throw new Error(result.message || "Failed to create blog.");

  return result;
};

export const updateBlog = async (id, formData) => {
  const token = localStorage.getItem("token");

  const response = await fetch(`${BASE_URL}/blog/updateBlog/${id}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const result = await response.json();
  if (!response.ok) throw new Error(result.message || "Failed to update blog.");

  return result;
};

export const updateBlogStatus = async (blogId, payload) => {
  try {
    const token = localStorage.getItem("token");
    const userEmail = await getUserEmailForLogging();

    const payloadWithEmail = { ...payload, updatedbyemail: userEmail };

    const response = await fetch(
      `${BASE_URL}/blog/updateBlogStatus/${blogId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payloadWithEmail),
      }
    );

    const result = await response.json();
    if (!response.ok)
      throw new Error(result.message || "Failed to update blog status.");

    return result;
  } catch (error) {
    throw new Error(error.message || "Failed to update blog status");
  }
};

// Other functions unchanged
export const fetchAllBlogs = async () => {
  const response = await fetch(`${BASE_URL}/blog/fetchAllBlogs`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch blogs");
  }
  const data = await response.json();
  return data.blogs;
};

export const fetchBlogById = async (id) => {
  const response = await fetch(`${BASE_URL}/blog/fetchBlogById/${id}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch blog");
  }
  return await response.json();
};

export const fetchBlogWithAuthor = async (id) => {
  try {
    const blogResponse = await fetch(`${BASE_URL}/blog/fetchBlogById/${id}`);
    if (!blogResponse.ok) throw new Error("Blog not found");
    const blogData = await blogResponse.json();

    const userResponse = await fetch(
      `${BASE_URL}/user/fetchUserById/${blogData.user_id}`
    );
    if (!userResponse.ok) throw new Error("Author not found");
    const userData = await userResponse.json();

    return { ...blogData, user: userData.user };
  } catch (error) {
    throw new Error(error.message || "Failed to fetch blog with author");
  }
};

export const fetchBlogsWithAuthors = async ({ id = null, parameter }) => {
  try {
    let url;

    if (["Department", "Faculty"].includes(parameter)) {
      url = `${BASE_URL}/blog/fetchAllBlogs?id=${id}&parameter=${parameter}`;
    } else {
      url = `${BASE_URL}/blog/fetchAllBlogs?parameter=${parameter}`;
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch blogs");

    const blogs = await response.json();

    const blogsWithUserData = await Promise.all(
      blogs.map(async (blog) => {
        try {
          const userRes = await fetch(
            `${BASE_URL}/user/fetchUserById/${blog.user_id}`
          );
          const userData = await userRes.json();

          return {
            ...blog,
            designation: userData.user?.user_designation || "Not Available",
            userImage: userData.user?.user_profile_img || "default.png",
          };
        } catch (err) {
          console.error(`Error fetching user ${blog.user_id}:`, err);
          return { ...blog, designation: "Unknown", userImage: "default.png" };
        }
      })
    );

    return blogsWithUserData;
  } catch (error) {
    console.error("Error fetching blogs with authors:", error);
    throw new Error(error.message || "Failed to fetch blogs with authors");
  }
};

export const fetchPendingBlogsWithAuthors = async ({
  id = null,
  parameter,
}) => {
  try {
    const url =
      parameter === "Department"
        ? `${BASE_URL}/blog/fetchPendingBlogs?id=${id}&parameter=${parameter}`
        : `${BASE_URL}/blog/fetchPendingBlogs?parameter=${parameter}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch blogs");
    const blogs = await response.json();

    const blogsWithUserData = await Promise.all(
      blogs.map(async (blog) => {
        try {
          const userRes = await fetch(
            `${BASE_URL}/user/fetchUserById/${blog.user_id}`
          );
          const userData = await userRes.json();
          return {
            ...blog,
            designation: userData.user?.user_designation || "Not Available",
            userImage: userData.user?.user_profile_img || "default.png",
          };
        } catch {
          return { ...blog, designation: "Unknown", userImage: "default.png" };
        }
      })
    );
    return blogsWithUserData;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to fetch blogs with authors");
  }
};
